import { useState, useEffect } from 'react';
import { Search, Grid, List, ChevronRight } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';
import { useUserStore } from '../../zustand/UserStore';
import { useHierarchicalUsersStore } from '../../zustand/HierarchicalUsersStore';
import { useNavigate } from 'react-router-dom';
import type { Representative } from '../../types/dashboard';

export default function TeamMembers() {
  const isDarkMode = useTheme((state) => state.theme);
  const user = useUserStore((state) => state.user);
  const { users, isLoading: usersLoading, fetchUsers } = useHierarchicalUsersStore();
  const nav = useNavigate();
  
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [repSearchQuery, setRepSearchQuery] = useState('');
  const [representatives, setRepresentatives] = useState<Representative[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch users data using shared store
  useEffect(() => {
    if (user) {
      fetchUsers();
    }
  }, [user, fetchUsers]);

  // Create representatives from users data (now includes call stats)
  useEffect(() => {
    if (!user || users.length === 0 || usersLoading) {
      setIsLoading(usersLoading);
      return;
    }
    
    try {
      setIsLoading(true);
      console.log(users);
      // Create representatives from users with call data already included
      const representativesData = users.map((u: any) => {
        console.log(u.total_calls);
        console.log(u.average_score);
        const totalCalls = u.total_calls || 0;
        const avgScore = u.average_score || 0;
        console.log(totalCalls);
        console.log(avgScore);
        
        
        return {
          id: u.uid,
          name: u.name,
          avatar: u.name.split(' ').map((n: string) => n[0]).join(''),
          role: u.role,
          designation: u.designation,
          team: 'Sales Team',
          totalCalls: totalCalls,
          avgScore: avgScore,
          lastActive: '2 hours ago',
          status: 'active' as const,
          trend: totalCalls > 0 ? 'up' : 'neutral',
          uid: u.uid,
          head: u.head,
          email: u.email || ''
        };
      });
      
      setRepresentatives(representativesData);
      
    } catch (error) {
      console.error('Error processing representatives:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, users, usersLoading]);

  const getVisibleRepresentatives = () => {
    if (!user || !Array.isArray(representatives)) return [];

    return representatives.filter(rep => {
      const repName = typeof rep.name === 'string' ? rep.name.toLowerCase() : '';
      const repRole = typeof rep.role === 'string' ? rep.role.toLowerCase() : '';
      const repDesignation = typeof rep.designation === 'string' ? rep.designation.toLowerCase() : '';
      const repEmail = typeof rep.email === 'string' ? rep.email.toLowerCase() : '';
      return repName.includes(repSearchQuery.toLowerCase()) || 
             repRole.includes(repSearchQuery.toLowerCase()) ||
             repDesignation.includes(repSearchQuery.toLowerCase()) ||
             repEmail.includes(repSearchQuery.toLowerCase());
    });
  };

  const visibleRepresentatives = getVisibleRepresentatives();

  const getStatusColor = (status: string): string => {
    return status === 'active' ? '#1a9f6b' : '#6b7280';
  };

  if (isLoading) {
    return (
      <div className={`rounded-xl p-6 mb-8 transition-all duration-300 ${
        isDarkMode 
          ? 'bg-[#0a1414] border border-[#1a2424]' 
          : 'bg-white border border-gray-200 shadow-sm'
      }`}>
        <div className="animate-pulse">
          <div className={`h-6 rounded mb-6 ${
            isDarkMode ? 'bg-[#1a2424]' : 'bg-gray-200'
          }`} style={{ width: '200px' }}></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`rounded-lg p-4 ${
                isDarkMode ? 'bg-[#1a2424]' : 'bg-gray-200'
              }`} style={{ height: '120px' }}></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (visibleRepresentatives.length === 0 && repSearchQuery.trim() === '') {
    return null;
  }

  // Show "No results found" when search returns empty results
  if (visibleRepresentatives.length === 0 && repSearchQuery.trim() !== '') {
    return (
      <div className={`rounded-xl p-6 mb-8 transition-all duration-300 ${
        isDarkMode 
          ? 'bg-[#0a1414] border border-[#1a2424]' 
          : 'bg-white border border-gray-200 shadow-sm'
      }`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h3 className={`text-xl font-bold ${
            isDarkMode ? 'text-white' : 'text-[#053333]'
          }`}>
            Team Members (0)
          </h3>
          <div className="flex gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-initial">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <input
                type="text"
                placeholder="Search reps..."
                value={repSearchQuery}
                onChange={(e) => setRepSearchQuery(e.target.value)}
                className={`w-full sm:w-64 pl-10 pr-4 py-2 rounded-lg outline-none transition-all duration-300 focus:ring-2 focus:ring-[#1a9f6b] ${
                  isDarkMode 
                    ? 'bg-[#050a0a] text-white border-2 border-[#1a2424] placeholder-gray-500' 
                    : 'bg-gray-100 text-[#053333] border-2 border-gray-300 placeholder-gray-400'
                }`}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  viewMode === 'grid' 
                    ? 'bg-[#1a9f6b] text-white' 
                    : isDarkMode 
                      ? 'bg-[#050a0a] text-gray-300 hover:bg-[#0f1919]' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  viewMode === 'list' 
                    ? 'bg-[#1a9f6b] text-white' 
                    : isDarkMode 
                      ? 'bg-[#050a0a] text-gray-300 hover:bg-[#0f1919]' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
        
        {/* No results message */}
        <div className={`text-center py-12 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          <Search className={`w-12 h-12 mx-auto mb-4 ${
            isDarkMode ? 'text-gray-500' : 'text-gray-400'
          }`} />
          <p className="text-lg mb-2">No team members found</p>
          <p className="text-sm">
            Try adjusting your search criteria for "{repSearchQuery}"
          </p>
          <button
            onClick={() => setRepSearchQuery('')}
            className={`mt-4 px-4 py-2 rounded-lg transition-all duration-300 ${
              isDarkMode 
                ? 'bg-[#1a9f6b] hover:bg-[#148856] text-white' 
                : 'bg-[#1a9f6b] hover:bg-[#148856] text-white'
            }`}
          >
            Clear Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl p-6 mb-8 transition-all duration-300 ${
      isDarkMode 
        ? 'bg-[#0a1414] border border-[#1a2424]' 
        : 'bg-white border border-gray-200 shadow-sm'
    }`}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h3 className={`text-xl font-bold ${
          isDarkMode ? 'text-white' : 'text-[#053333]'
        }`}>
          Team Members ({visibleRepresentatives.length})
        </h3>
        <div className="flex gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              placeholder="Search reps..."
              value={repSearchQuery}
              onChange={(e) => setRepSearchQuery(e.target.value)}
              className={`w-full sm:w-64 pl-10 pr-4 py-2 rounded-lg outline-none transition-all duration-300 focus:ring-2 focus:ring-[#1a9f6b] ${
                isDarkMode 
                  ? 'bg-[#050a0a] text-white border-2 border-[#1a2424] placeholder-gray-500' 
                  : 'bg-gray-100 text-[#053333] border-2 border-gray-300 placeholder-gray-400'
              }`}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all duration-300 ${
                viewMode === 'grid' 
                  ? 'bg-[#1a9f6b] text-white' 
                  : isDarkMode 
                    ? 'bg-[#050a0a] text-gray-300 hover:bg-[#0f1919]' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all duration-300 ${
                viewMode === 'list' 
                  ? 'bg-[#1a9f6b] text-white' 
                  : isDarkMode 
                    ? 'bg-[#050a0a] text-gray-300 hover:bg-[#0f1919]' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Representatives Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {visibleRepresentatives.map((rep) => (
            <div
              key={rep.id}
              className={`rounded-lg p-4 cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${
                isDarkMode 
                  ? 'bg-[#050a0a] border border-[#1a2424] hover:border-[#1a9f6b]' 
                  : 'bg-gray-50 border border-gray-200 hover:border-[#1a9f6b] shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold bg-[#1a9f6b] text-white">
                      {rep.avatar}
                    </div>
                  </div>
                  <div>
                    <h4 className={`font-semibold ${
                      isDarkMode ? 'text-white' : 'text-[#053333]'
                    }`}>
                      {rep.name}
                    </h4>
                    <p className={`text-xs ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {rep.designation || rep.role}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Team:</span>
                  <span className={isDarkMode ? 'text-white' : 'text-[#053333]'}>{rep.team}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Calls:</span>
                  <span className={isDarkMode ? 'text-white' : 'text-[#053333]'}>{rep.totalCalls}</span>
                </div>
                {/* <div className="flex justify-between">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Score:</span>
                  <span style={{ color: getScoreColor(rep.avgScore) }} className="font-semibold">{rep.avgScore}</span>
                </div> */}
              </div>

              <button 
                onClick={() => nav(`/user/${rep.id}`)} 
                className="w-full cursor-pointer mt-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 bg-[#1a9f6b] hover:bg-[#148856] text-white hover:translate-y-[-1px]"
              >
                View Profile
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {visibleRepresentatives.map((rep) => (
            <div
              key={rep.id}
              className={`rounded-lg p-4 flex items-center justify-between cursor-pointer transition-all duration-300 hover:translate-x-1 ${
                isDarkMode 
                  ? 'bg-[#050a0a] border border-[#1a2424] hover:border-[#1a9f6b]' 
                  : 'bg-gray-50 border border-gray-200 hover:border-[#1a9f6b]'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold bg-[#1a9f6b] text-white">
                    {rep.avatar}
                  </div>
                  <div 
                    className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${
                      isDarkMode ? 'border-[#0a1414]' : 'border-white'
                    }`}
                    style={{ backgroundColor: getStatusColor(rep.status) }}
                  />
                </div>
                <div>
                  <h4 className={`font-semibold ${
                    isDarkMode ? 'text-white' : 'text-[#053333]'
                  }`}>
                    {rep.name}
                  </h4>
                  <p className={`text-sm ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {rep.designation || rep.role} • {rep.team}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className={`text-lg font-semibold ${
                    isDarkMode ? 'text-white' : 'text-[#053333]'
                  }`}>
                    {rep.totalCalls}
                  </div>
                  <div className={`text-xs ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Calls</div>
                </div>
                <div className="text-center">
                  {/* <div className={`text-lg font-semibold`} style={{ color: getScoreColor(rep.avgScore) }}>
                    {rep.avgScore}
                  </div> */}
                  <div className={`text-xs ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Score</div>
                </div>
                <button 
                  onClick={() => nav(`/user/${rep.id}`)}
                  className={`p-2 rounded-lg transition-all duration-300 ${
                    isDarkMode 
                      ? 'hover:bg-[#050a0a] text-gray-300' 
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
